import * as PIXI from "pixi.js";

import { StateMachine } from "@src/core/state-machine/StateMachine";
import { EventSystem } from "@src/core/events/EventSystem";
import { TGameUIButtonType } from "@src/game/ui/GameUI";
import { IView } from "@src/core/game/components/IView";
import { constants } from "@src/game/Constants";

enum EButtonState {
  ACTIVE = "active",
  INACTIVE = "inactive",
}

// Currently this class behaves more like a toggle button
// Either keep it that way or make it a more generic Button class
// This class fires a click event with the button type and optional arguments.
export class Button implements IView {
  private _view: PIXI.Container = new PIXI.Container();
  public get view(): PIXI.Container {
    return this._view;
  }

  public get width(): number {
    return this._background.width;
  }

  public get enabled(): boolean {
    return this._isActive;
  }

  // NOTE: Add more states if necessary
  private _activeGraphics?: PIXI.Texture | PIXI.Graphics;
  private _inactiveGraphics?: PIXI.Texture | PIXI.Graphics;

  public set activeGraphics(value: PIXI.Texture | PIXI.Graphics | undefined) {
    this._activeGraphics = value;
    this._stateMachine.setBool("isEnabled", !this._isActive);
    this._stateMachine.setBool("isEnabled", this._isActive);
  }

  public get activeGraphics(): PIXI.Texture | PIXI.Graphics | undefined {
    return this._activeGraphics;
  }

  public set inactiveGraphics(value: PIXI.Texture | PIXI.Graphics | undefined) {
    this._inactiveGraphics = value;
    this._stateMachine.setBool("isEnabled", !this._isActive);
    this._stateMachine.setBool("isEnabled", this._isActive);
  }

  public get inactiveGraphics(): PIXI.Texture | PIXI.Graphics | undefined {
    return this._inactiveGraphics;
  }
  // Used for global EventSystem click event args
  public clickArgs?: any;

  private _background: PIXI.Container = new PIXI.Container();
  private _buttonType: TGameUIButtonType;

  // Separate onClick event handler for custom logic
  public onClick: (() => void) | null = null;

  private _stateMachine: StateMachine<EButtonState>;
  private _isActive: boolean;

  constructor(buttonType: TGameUIButtonType, private isToggle: boolean = true) {
    this._buttonType = buttonType;

    this._stateMachine = new StateMachine<EButtonState>(EButtonState.ACTIVE);
    this._isActive = true;
    this._stateMachine.setBool("isEnabled", this._isActive);

    this._setupButtonStates();
    this._initializeAssets();
    this._setupEventListeners();
    this._parentAssets();
  }

  public setActive(enabled: boolean): void {
    this._isActive = enabled;
    this._stateMachine.setBool("isEnabled", this._isActive);
  }

  public setVisible(visible: boolean): void {
    this._background.visible = visible;
  }

  public isVisible(): boolean {
    return this._background.visible;
  }

  public setScale(x: number, y?: number): void {
    this._background.scale.set(x, y ?? x);
  }

  private _setupButtonStates(): void {
    this._stateMachine.addState(EButtonState.ACTIVE, {
      onEnter: () => {
        this._setBackgroundGraphics(this._activeGraphics);
      },
      transitions: [
        {
          toState: EButtonState.INACTIVE,
          conditions: [{ type: "bool", parameter: "isEnabled", value: false }],
        },
      ],
    });

    this._stateMachine.addState(EButtonState.INACTIVE, {
      onEnter: () => {
        this._setBackgroundGraphics(this._activeGraphics);
        this._setBackgroundGraphics(
          this._inactiveGraphics ?? this._activeGraphics,
        );
      },
      transitions: [
        {
          toState: EButtonState.ACTIVE,
          conditions: [{ type: "bool", parameter: "isEnabled", value: true }],
        },
      ],
    });
  }

  private _setupEventListeners(): void {
    this._background.on("pointerdown", () => {
      if (this.isToggle) {
        this._isActive = !this._isActive;
        this._stateMachine.setBool("isEnabled", this._isActive);
      }

      if (this.onClick) {
        this.onClick();
      }

      EventSystem.invoke(
        constants.UIEvents.CLICKED_UI_BUTTON,
        this._buttonType,
        this.clickArgs,
      );
    });
  }

  private _initializeAssets() {
    this._setBackgroundGraphics(this.activeGraphics);
    this._background.eventMode = "static";
  }

  private _parentAssets() {
    this._view.addChild(this._background);
  }

  private _setBackgroundGraphics(
    graphics: PIXI.Texture | PIXI.Graphics | undefined,
  ): void {
    if (!graphics) {
      return;
    }

    this._background.children.forEach((child) => {
      this._background.removeChild(child);
    });

    if (graphics instanceof PIXI.Texture) {
      const sprite: PIXI.Sprite = new PIXI.Sprite(graphics);
      sprite.anchor.set(0.5);
      this._background.addChild(sprite);
    } else {
      this._background.addChild(graphics);
    }
  }
}
